import { FormControl, Grid, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  AddQuizTitlesCall,
  DeleteQuizTitlesCall,
  EditQuizTitlesCall,
  GetQuizTitlesCall,
} from "../../redux/api/quizTitle.api";
import { setLang } from "../../redux/slices/generalSlice";
import { store } from "../../redux/store";
import { basicURL } from "../../apiManager/APIConfig";
import axios from "axios";

const QuizTitles = () => {
  const [items, setItems] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [message, setMessage] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const lang = useSelector((state) => state.general?.lang);
  const [type] = useState("histology");
  const theme = useTheme();
  const imageInputRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cardBg = theme.palette.background?.card ?? theme.palette.background?.paper ?? theme.palette.background?.default;
  const textPrimary = theme.palette.text.primary;
  const isDark = theme.palette.mode === "dark";
  const inputBg = isDark ? (theme.palette.grey?.[800] ?? "#2d2d2d") : theme.palette.background.paper;
  const inputBorder = theme.palette.divider;
  const inputTextColor = theme.palette.text.primary;
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: inputBg,
      color: inputTextColor,
      "& fieldset": { borderColor: inputBorder },
      "&:hover fieldset": { borderColor: "primary.main" },
      "&.Mui-focused fieldset": { borderColor: "primary.main" },
      "& input": { color: inputTextColor },
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
    "& .MuiInputLabel-root.Mui-focused": { color: "primary.main" },
  };

  const fetchTitles = async () => {
    const res = await GetQuizTitlesCall({ lang, type });
    if (res?.status === 200) {
      setItems(res.data);
    }
  };

  const handleLanguageChange = (event) => {
    // setLanguage(event.target.value);
    dispatch(setLang(event.target.value));
    fetchTitles();
  };

  const handleAddQuestion = (id) => {
    navigate(`/showQuestions/${id}/${lang}`);
  };

  useEffect(() => {
    fetchTitles();
  }, []);

  const handleAddTitle = async () => {
    const trimmed = (newTitle || "").trim();
    if (!trimmed) {
      setMessage("Please enter a title");
      return;
    }
    setMessage("");
    const res = await AddQuizTitlesCall({
      title: trimmed,
      lang,
      type,
    });
    if (res?.status === 201) {
      setMessage("Title added successfully!");
      setNewTitle("");
      fetchTitles();
    } else {
      setMessage(res?.message || "Failed to add title");
    }
  };

  const handleEdit = async (id, newValue) => {
    const res = await EditQuizTitlesCall({ title_id: id, title: newValue, lang, type });
    if (res?.status === 200) fetchTitles();
  };
  const handleTriggerImageUpload = (titleId) => {
    imageInputRef.current?.setAttribute?.("data-title-id", titleId);
    imageInputRef.current?.click?.();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    const titleId = imageInputRef.current?.getAttribute?.("data-title-id");
    if (!file || !titleId) return;
    e.target.value = "";
    try {
      setUploadingId(titleId);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("entity_type", "quiz_title");
      formData.append("entity_id", titleId);
      const token = store.getState().auth?.access_token;
      const res = await axios.post(`${basicURL}file/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const downloadUrl = res?.data?.download_url;
      if (downloadUrl) {
        const editRes = await EditQuizTitlesCall({ title_id: titleId, image: downloadUrl, lang, type });
        if (editRes?.status === 200) fetchTitles();
      }
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteTitle = async (id) => {
    if (window.confirm("Are you sure you want to delete this title?")) {
      const res = await DeleteQuizTitlesCall({ title_id: id });
      if (res?.status === 200) {
        setMessage("Deleted successfully!");
        fetchTitles();
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={0} pt={3} pb={3}>
        <MDBox py={1} display="flex" justifyContent="flex-end" mb={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <TextField
                size="small"
                label="New title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddTitle()}
                placeholder="Enter title name"
                sx={{ minWidth: 220, ...inputSx }}
              />
          </Grid>
          <Grid item>
            <MDButton color="info" variant="contained" onClick={() => handleAddTitle()}>
              + Add Title
            </MDButton>
          </Grid>
          {message && (
            <Grid item xs={12} sm="auto">
              <span style={{ color: message.includes("success") ? "green" : "inherit" }}>{message}</span>
            </Grid>
          )}
          {/* Language Dropdown on the Right */}
          <Grid
            item
            xs
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <FormControl sx={{ width: 250, "& .MuiInputBase-root": { minHeight: 48 } }}>
              <InputLabel>Language</InputLabel>
              <Select value={lang} onChange={handleLanguageChange} sx={{ minHeight: 48 }}>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="pt">Portugal</MenuItem>
                <MenuItem value="es">Spanish</MenuItem>
                <MenuItem value="it">Italian</MenuItem>
                <MenuItem value="ge">Germany</MenuItem>
                <MenuItem value="nl">Dutch</MenuItem>
                <MenuItem value="cn">Chines</MenuItem>
                <MenuItem value="ja">Japanese</MenuItem>
                <MenuItem value="ar">Arabic</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox py={2} px={3}>
        <input
          type="file"
          ref={imageInputRef}
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageUpload}
        />
        <Grid container spacing={3}>
          {items.map((t, index) => (
            <MDBox
              key={index}
              p={2}
              display="flex"
              flexDirection="column"
              alignItems="center"
              borderRadius="8px"
                style={{ marginBottom: 15, backgroundColor: cardBg, marginRight:10 }}
            >
              {t?.image ? (
                <img
                  src={t.image}
                  alt=""
                  style={{
                    width: 100,
                    height: "auto",
                    borderRadius: "8px",
                    marginBottom: "10px",
                    objectFit: "cover",
                    minHeight: 60,
                  }}
                />
              ) : (
                <MDBox
                  sx={{
                    width: 100,
                    minHeight: 60,
                    borderRadius: "8px",
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "action.hover",
                    color: "text.secondary",
                  }}
                >
                  <AddPhotoAlternateIcon sx={{ fontSize: 80 }} />
                </MDBox>
              )}
              <li style={{ display: "flex", flexDirection: "column", listStyle: "none", width: "100%" }} key={t.id}>
                <label
                  style={{
                    display: "block",
                    fontSize: 14,
                    marginTop: 10,
                    marginBottom: 5,
                    fontWeight: "bold",
                    color: textPrimary,
                  }}
                >
                  Title
                </label>
                <TextField
                  size="small"
                  defaultValue={t.title}
                  onBlur={(e) => handleEdit(t.id, e.target.value)}
                  fullWidth
                  sx={{ mt: 0.5, ...inputSx }}
                />
                <label
                  style={{
                    display: "block",
                    fontSize: 14,
                    marginTop: 20,
                    marginBottom: 5,
                    fontWeight: "bold",
                    color: textPrimary,
                  }}
                >
                  Image
                </label>
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  disabled={uploadingId === t.id}
                  onClick={() => handleTriggerImageUpload(t.id)}
                  sx={{ mt: 0.5 }}
                >
                  {uploadingId === t.id ? "Uploading…" : "Upload image"}
                </MDButton>
              </li>
              <div style={{ gap: 10, display: "flex", flexDirection: "row" }}>
                <button
                  style={{
                    backgroundColor: "red",
                    color: "white",
                    borderWidth: 0,
                    borderRadius: 6,
                    marginTop: 20,
                    padding: 8,
                  }}
                  onClick={() => handleDeleteTitle(t.id)}
                >
                  Delete
                </button>
                <button
                  style={{
                    backgroundColor: "green",
                    color: "white",
                    borderWidth: 0,
                    borderRadius: 6,
                    marginTop: 20,
                    padding: 8,
                  }}
                  onClick={() => handleAddQuestion(t.id)}
                >
                  Add Questions
                </button>
              </div>
            </MDBox>
          ))}
        </Grid>
      </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default QuizTitles;
